/**
 * Fold-line presets (Page Design ▸ Guides ▸ Fold Lines). Each preset places `kind: 'fold'` guides that print as dashed fold
 * marks (Export / Print ▸ Fold marks) and show as dashed lines on the page. Folds run across the long side of the page: a
 * landscape page gets vertical fold lines dividing its width, a portrait page gets horizontal ones dividing its height.
 *
 * Panel margins (Publisher's brochure layout guides): a fold guide can carry a `gutter` (px). Two panel-margin guides are then
 * derived on either side of the crease (`panelGuidesOf`) — they show on the page and objects snap to them, so text stays clear
 * of the fold. They are derived, never stored, so they follow the fold wherever it is dragged.
 */
export const FOLD_PRESETS = [
  { id: 'bifold', label: 'Bi-fold', fractions: [1 / 2], hint: 'One fold at the centre — 2 panels a side (booklets, greeting cards)' },
  { id: 'trifold', label: 'Tri-fold (letter fold)', fractions: [1 / 3, 2 / 3], hint: 'Two folds at thirds — 3 panels a side; the brochure templates use this' },
  { id: 'zfold', label: 'Z-fold (accordion)', fractions: [1 / 3, 2 / 3], hint: 'The same thirds as a tri-fold, folded in alternating directions' },
  { id: 'gatefold', label: 'Gate fold', fractions: [1 / 4, 3 / 4], hint: 'Two flaps that meet at the centre — 4 panels a side' },
];

/** Panel-margin widths offered next to each fold line (px at 96 dpi). */
export const PANEL_GUTTERS = [
  { value: 0, label: 'None' },
  { value: 12, label: '⅛ in' },
  { value: 24, label: '¼ in' },
  { value: 48, label: '½ in' },
];
/** Publisher's brochure templates keep ¼ in between panel content and the crease. */
export const DEFAULT_PANEL_GUTTER = 24;
export const normalizeGutter = (g) => (PANEL_GUTTERS.some((o) => o.value === Number(g)) ? Number(g) : DEFAULT_PANEL_GUTTER);
export const gutterLabel = (g) => PANEL_GUTTERS.find((o) => o.value === Number(g))?.label || `${(Number(g) / 96).toFixed(2)} in`;

export const isFoldGuide = (g) => !!g && g.kind === 'fold';
export const isPanelGuide = (g) => !!g && g.kind === 'panel';

/** Direction of the fold lines for a page: 'vertical' (across the width) for landscape / square pages, else 'horizontal'. */
export const foldOrientationFor = (page, orientation = 'auto') => {
  if (orientation === 'vertical' || orientation === 'horizontal') return orientation;
  return page.width >= page.height ? 'vertical' : 'horizontal';
};

/** Fold guides for a preset on a page (positions in canvas px, rounded to whole px), with panel margins of `gutter` px. */
export const foldGuidesForPreset = (preset, page, orientation = 'auto', gutter = DEFAULT_PANEL_GUTTER) => {
  const o = foldOrientationFor(page, orientation);
  const size = o === 'vertical' ? page.width : page.height;
  const stamp = Date.now();
  return preset.fractions.map((f, i) => ({ id: `fold-${preset.id}-${i + 1}-${stamp}`, orientation: o, position: Math.round(size * f), kind: 'fold', gutter }));
};

/** The panel margin currently in use: the gutter shared by the fold guides, else the default. */
export const currentPanelGutter = (guides = []) => {
  const folds = guides.filter(isFoldGuide);
  if (!folds.length) return DEFAULT_PANEL_GUTTER;
  const g = Number(folds[0].gutter ?? 0);
  return folds.every((f) => Number(f.gutter ?? 0) === g) ? g : DEFAULT_PANEL_GUTTER;
};

/**
 * Derived panel-margin guides: for every fold guide with a gutter, one guide on each side of the crease (inside the page).
 * `foldId` links them back to their fold line.
 */
export const panelGuidesOf = (guides = [], page) => {
  const out = [];
  guides.forEach((g) => {
    if (!isFoldGuide(g) || !g.orientation) return;
    const gutter = Number(g.gutter) || 0;
    if (gutter <= 0) return;
    const size = page ? (g.orientation === 'vertical' ? page.width : page.height) : Infinity;
    const pos = Number(g.position);
    [[-1, 'before'], [1, 'after']].forEach(([dir, side]) => {
      const p = pos + dir * gutter;
      if (p <= 0 || p >= size) return;
      out.push({ id: `${g.id}-panel-${side}`, orientation: g.orientation, position: p, kind: 'panel', foldId: g.id, gutter });
    });
  });
  return out;
};
